import React from "react";
import { _cs } from "@togglecorp/fujs";

import { Translation } from "react-i18next";
import LoadingAnimation from "#rscv/LoadingAnimation";
import List from "#rscv/List";
import { arrayGroupBy } from "#utils/common";

import {
  createConnectedRequestCoordinator,
  createRequestClient,
  NewProps,
  ClientAttributes,
  methods,
} from "#request";
import Message from "#rscv/Message";

import ManualIcon from "#resources/images/manualicon.png";
import styles from "./styles.module.scss";

interface OwnProps {
  className?: string;
}

interface Params {
  setManualItems?: (items: ManualElement[]) => void;
}

type Props = NewProps<OwnProps, Params>;

interface State {
  manualItems: ManualElement[];
}

interface ManualElement {
  id: number;
  title: string;
  description?: string;
  file: string;
  image?: string;
  year?: number | string;
  order?: number;
}

interface ManualResponse {
  results?: ManualElement[];
}

const manualSortFn = (a: ManualElement, b: ManualElement) => {
  const firstOrder =
    typeof a.order === "number" ? a.order : Number.POSITIVE_INFINITY;
  const secondOrder =
    typeof b.order === "number" ? b.order : Number.POSITIVE_INFINITY;

  if (firstOrder !== secondOrder) {
    return firstOrder - secondOrder;
  }

  return a.title.localeCompare(b.title);
};

const ManualItem = ({
  data,
  className,
}: {
  className?: string;
  data: ManualElement;
}) => (
  <a
    href={data.file}
    className={_cs(className, styles.manualItem)}
    download
    target="_blank"
    rel="noopener noreferrer"
    title="Click to download"
  >
    <div className={styles.left}>
      <img src={data.image || ManualIcon} alt="M" className={styles.image} />
    </div>
    <div className={styles.right}>
      <div className={styles.title}>{data.title}</div>
      {data.description && (
        <div className={styles.description}>{data.description}</div>
      )}
    </div>
  </a>
);

const manualItemEmptyList: ManualElement[] = [];
const manualItemKeySelector = (item: ManualElement) => item.id;

const requests: { [key: string]: ClientAttributes<Props, Params> } = {
  manualListRequest: {
    url: "/manual/",
    method: methods.GET,
    onMount: true,
    onSuccess: ({ response, params }) => {
      const typedResponse = response as ManualResponse | ManualElement[];
      const results = Array.isArray(typedResponse)
        ? typedResponse
        : (typedResponse.results ?? manualItemEmptyList);

      params?.setManualItems?.(results);
    },
  },
};

class Manual extends React.PureComponent<Props, State> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      manualItems: manualItemEmptyList,
    };

    const {
      requests: { manualListRequest },
    } = this.props;

    manualListRequest.setDefaultParams({
      setManualItems: this.handleSetManualItems,
    });
  }

  private handleSetManualItems = (manualItems: ManualElement[]) => {
    this.setState({ manualItems });
  };

  private manualItemModifier = (key: React.Key, item: ManualElement) => (
    <ManualItem key={key} data={item} />
  );

  public render() {
    const {
      requests: {
        manualListRequest: { pending },
      },
      className,
    } = this.props;
    const { manualItems } = this.state;

    // grouping by year and removing undefined year values
    const yearWiseList = arrayGroupBy(manualItems, "year");
    delete yearWiseList[`${undefined}`];
    const hasYearGroupedData = Object.keys(yearWiseList).length > 0;
    // previous logic
    /* return (
            <div className={_cs(className, styles.manual)}>
                { pending && <LoadingAnimation /> }
                <List
                    data={results}
                    renderer={ManualItem}
                    rendererParams={manualItemRendererParams}
                    keySelector={manualItemKeySelector}
                />
            </div>
        ); */

    return (
      <div className={_cs(className, styles.manual)}>
        {pending && (
          <div className={styles.message}>
            <LoadingAnimation />
          </div>
        )}
        {!pending && manualItems.length === 0 && (
          <div className={styles.message}>
            <Translation>
              {(t) => <Message>{t("No Publications to display")}</Message>}
            </Translation>
          </div>
        )}
        {!pending && manualItems.length > 0 && !hasYearGroupedData && (
          <List
            data={[...manualItems].sort(manualSortFn)}
            keySelector={manualItemKeySelector}
            modifier={this.manualItemModifier}
          />
        )}
        {!pending &&
          manualItems.length > 0 &&
          hasYearGroupedData &&
          Object.keys(yearWiseList)
            .sort((a, b) => Number(b) - Number(a))
            .map((key) => {
              const orderedManauals = [...yearWiseList[key]].sort(manualSortFn);
              return (
                <div key={key} className={styles.item}>
                  <div className={styles.header}>{key}</div>
                  <List
                    data={orderedManauals}
                    keySelector={manualItemKeySelector}
                    modifier={this.manualItemModifier}
                  />
                </div>
              );
            })}
      </div>
    );
  }
}

export default createConnectedRequestCoordinator<Props>()(
  createRequestClient(requests)(Manual)
);
